<template>
  <div class="relative">
    <div class="flex justify-between items-start">
      <div>
        <h3 class="text-xl font-bold text-neutral-900">{{ $t('API Access') }}</h3>
        <p class="text-neutral-600 mt-1">
          {{ $t('Create project-scoped API keys to manage boards and items programmatically') }}<template
            v-if="mcpAvailable"
          >{{ $t(', or connect AI assistants like Claude Code, Claude Desktop, and Cursor via MCP') }}</template>.
        </p>
      </div>
    </div>

    <div class="toggle-wrapper mt-6">
      <div
        class="button--toggle"
        :class="{ 'area--disabled': isUpdatingSetting }"
      >
        <input
          type="checkbox"
          class="checkbox"
          @change="toggleApiEnabled"
          v-model="apiEnabledState"
        />
        <div class="knobs"></div>
        <div class="layer"></div>
      </div>
      <transition name="fromLeft" mode="out-in" appear>
        <span v-if="!isUpdatingSetting" key="loaded">
          <span v-if="apiEnabledState" key="isactive" class="flex items-center">
            <span><strong>{{ $t('On') }}</strong> {{ $t('– API access is enabled') }}</span>
          </span>
          <span v-else key="isinactive" class="flex items-center">
            <span><strong>{{ $t('Off') }}</strong> {{ $t('– API access is disabled') }}</span>
          </span>
        </span>
        <span class="text--lighter" v-else key="loading">{{ $t('Saving ...') }}</span>
      </transition>
    </div>

    <p v-if="toggleError" class="text-red-600 text-sm mt-2">{{ toggleError }}</p>

    <!-- AI Assistants (MCP) — hidden until launch (MCP_SERVER_ENABLED, see docs/MCP_LAUNCH.md) -->
    <div class="mt-8 p-5 border rounded-xl" v-if="mcpAvailable">
      <h4 class="text-lg font-semibold text-neutral-900">{{ $t('AI Assistants (MCP)') }}</h4>
      <p class="text-sm text-neutral-600 mt-1">
        {{ $t('Connect Claude Code, Claude Desktop, Cursor, or VS Code to this project so your assistant can search and summarize feedback, read comments, and read your changelog — and, with a full-access key, create posts, change statuses, and reply to customers. ChatGPT cannot connect yet; the ChatGPT tab below explains why.') }}
      </p>

      <p
        v-if="!apiEnabledState"
        class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 mt-3"
      >
        {{ $t('Enable API access above first — the MCP server uses the same toggle and keys.') }}
      </p>

      <div class="mt-4">
        <label class="label">{{ $t('MCP server URL') }}</label>
        <div class="flex flex-wrap items-center gap-2 mt-1">
          <code class="px-3 py-2 bg-neutral-100 rounded text-sm break-all">{{ mcpUrl }}</code>
          <button type="button" class="btn-secondary" @click="copySnippet(mcpUrl, 'mcp-url')">
            {{ copiedSnippet === 'mcp-url' ? $t("Copied!") : $t("Copy") }}
          </button>
        </div>
        <p class="text-xs text-neutral-500 mt-2">
          {{ $t('This URL is the same for every FeedBear project — your API key is what selects the project. Use it exactly as shown, even if your board runs on a subdomain or your own custom domain.') }}
        </p>
      </div>

      <h4 class="mt-6 font-semibold text-neutral-900">{{ $t('1. Create a key for your assistant') }}</h4>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
        <div class="border rounded-lg p-4">
          <button
            type="button"
            class="btn-main"
            :disabled="isCreatingKey || !apiEnabledState"
            @click="createMcpKey('read')"
          >
            {{ isCreatingKey ? $t("Creating...") : $t("Create read-only key") }}
          </button>
          <p class="text-sm text-neutral-600 mt-2">
            <strong>{{ $t('Recommended.') }}</strong> {{ $t('Lets the assistant search, read and summarize your feedback, comments, statuses and changelog. It cannot change anything in your project.') }}
          </p>
        </div>
        <div class="border rounded-lg p-4">
          <button
            type="button"
            class="bg-white border border-gray-300 text-neutral-800 hover:bg-gray-50 rounded-lg shadow-sm px-4 py-2 text-sm font-medium whitespace-nowrap"
            :class="{ 'area--disabled': isCreatingKey || !apiEnabledState }"
            :disabled="isCreatingKey || !apiEnabledState"
            @click="createMcpKey('full')"
          >
            {{ isCreatingKey ? $t("Creating...") : $t("Create full-access key") }}
          </button>
          <p class="text-sm text-neutral-600 mt-2">
            {{ $t('Everything above, plus the assistant can create and edit posts, change statuses, mark posts as spam, draft changelog entries, and') }} <strong>{{ $t('publish public comments in your name') }}</strong>{{ $t('. Only choose this if you want it to write.') }}
          </p>
        </div>
      </div>
      <p v-if="mcpError" class="text-red-600 text-sm mt-2">{{ mcpError }}</p>
      <p class="text-xs text-neutral-500 mt-2">
        {{ $t('The token is shown once, right after the key is created, together with a ready-to-paste setup snippet for your client. Keys created before the comment and changelog scopes existed need to be rotated or re-created to grant them.') }}
      </p>

      <div class="mt-5 p-4 rounded-lg bg-amber-50 border border-amber-200">
        <p class="text-sm font-semibold text-amber-900">{{ $t('Before you hand an assistant a key') }}</p>
        <ul class="list-disc ml-5 mt-2 text-sm text-amber-800 space-y-1">
          <li>
            <strong>{{ $t('Comments are public and are emailed.') }}</strong>
            <code class="bg-neutral-100 px-1 rounded text-xs">feedbear_add_comment</code> {{ $t('publishes a normal comment on the post, attributed to the team member who created the key, and notifies everyone following that post. Creating and updating posts is silent; commenting is not.') }}
          </li>
          <li>
            <strong>{{ $t('Private boards are reachable.') }}</strong> {{ $t('The board, post and comment tools accept an') }}
            <code class="bg-neutral-100 px-1 rounded text-xs">include_private</code> {{ $t('option, so an assistant can be asked to read — or write to — your private, team-only boards.') }}
          </li>
          <li>
            <strong>{{ $t('The key is tied to whoever created it.') }}</strong> {{ $t('If that team member is removed from the project, commenting stops working. Rotate the key from your own account to take it over.') }}
          </li>
          <li>
            <strong>{{ $t('A read-only key hides the write tools rather than erroring.') }}</strong> {{ $t('The assistant simply will not have them, so it will say it cannot do something instead of showing a failure.') }}
          </li>
        </ul>
      </div>

      <h4 class="mt-6 font-semibold text-neutral-900">{{ $t('What each key lets the assistant do') }}</h4>
      <div class="mt-2" v-for="group in mcpToolGroups" :key="group.title">
        <p class="text-sm font-semibold text-neutral-700 mt-3">
          {{ group.title }}
          <span class="font-normal text-neutral-500">— {{ group.scopes }}</span>
        </p>
        <ul class="mt-1 text-sm text-neutral-600 space-y-1">
          <li v-for="tool in group.tools" :key="tool.name">
            <code class="bg-neutral-100 px-1 rounded text-xs">{{ tool.name }}</code>
            — {{ tool.label }}
          </li>
        </ul>
      </div>

      <h4 class="mt-6 font-semibold text-neutral-900">{{ $t('2. Set up your client') }}</h4>
      <p class="text-sm text-neutral-600 mt-1" v-if="!createdToken">
        {{ $t('Create a key above and these snippets fill in your real token automatically. If you already have a token, replace') }} <code class="bg-neutral-100 px-1 rounded">YOUR_API_TOKEN</code> {{ $t('with it — tokens are only ever shown once, when a key is created or rotated.') }}
      </p>

      <div class="mt-3">
        <TabGroup wrap key-name="id" :tabs="mcpClients" :value="mcpClientTab" @input="(v) => mcpClientTab = v" />

        <div class="mt-4" v-if="mcpClientTab === 'claude-code'">
          <p class="text-sm text-neutral-600">
            {{ $t('Run this in your terminal, then run') }} <code class="bg-neutral-100 px-1 rounded">/mcp</code>
            {{ $t('inside Claude Code and check that') }} <strong>feedbear</strong> {{ $t('shows as connected:') }}
          </p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="mcpClaudeCodeSnippet" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
          <button type="button" class="btn-secondary mt-2" @click="copySnippet(mcpClaudeCodeSnippet, 'claude-code')">
            {{ copiedSnippet === 'claude-code' ? $t("Copied!") : $t("Copy command") }}
          </button>
        </div>

        <div class="mt-4" v-if="mcpClientTab === 'claude-desktop'">
          <p class="text-sm text-neutral-600">
            {{ $t('Claude Desktop on a personal plan cannot send an API-key header to a remote server, so it connects through the') }} <code class="bg-neutral-100 px-1 rounded">mcp-remote</code> {{ $t('bridge. Node.js must be installed.') }}
          </p>
          <ol class="list-decimal ml-5 text-sm text-neutral-600 space-y-1 mt-2">
            <li>{{ $t('Open') }} <strong>{{ $t('Settings → Developer → Edit Config') }}</strong> {{ $t('to reveal') }} <code class="bg-neutral-100 px-1 rounded">claude_desktop_config.json</code>.</li>
            <li>{{ $t('Paste the block below, merging it into your existing') }} <code class="bg-neutral-100 px-1 rounded">mcpServers</code> {{ $t('object if you already have one.') }}</li>
            <li>{{ $t('Quit Claude Desktop completely, then reopen it.') }}</li>
          </ol>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="mcpClaudeDesktopSnippet" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
          <button type="button" class="btn-secondary mt-2" @click="copySnippet(mcpClaudeDesktopSnippet, 'claude-desktop')">
            {{ copiedSnippet === 'claude-desktop' ? $t("Copied!") : $t("Copy Claude Desktop JSON") }}
          </button>
          <p class="text-xs text-neutral-500 mt-2">
            {{ $t('The token sits in') }} <code class="bg-neutral-100 px-1 rounded">env</code> {{ $t('rather than inline because some clients mishandle spaces inside') }} <code class="bg-neutral-100 px-1 rounded">args</code>.
          </p>

          <p class="text-sm text-neutral-600 mt-4">
            <strong>{{ $t('On a Claude Team or Enterprise plan') }}</strong> {{ $t('an organization admin can skip the bridge and add FeedBear natively under') }} <strong>{{ $t('Settings → Connectors → Add custom connector') }}</strong>{{ $t(', using the MCP server URL above plus a request header named') }}
            <code class="bg-neutral-100 px-1 rounded">Authorization</code> {{ $t('with this value:') }}
          </p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="mcpAuthHeaderSnippet" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
          <button type="button" class="btn-secondary mt-2" @click="copySnippet(mcpAuthHeaderSnippet, 'claude-header')">
            {{ copiedSnippet === 'claude-header' ? $t("Copied!") : $t("Copy header value") }}
          </button>
          <p class="text-xs text-neutral-500 mt-2">
            {{ $t('Custom-header authentication in Claude connectors is in beta, is entered by an organization admin, and is shared by the whole organization. On a personal plan there is no header field — use the bridge above.') }}
          </p>
        </div>

        <div class="mt-4" v-if="mcpClientTab === 'cursor'">
          <p class="text-sm text-neutral-600">
            {{ $t('Add to') }} <code class="bg-neutral-100 px-1 rounded">.cursor/mcp.json</code>
            {{ $t('(this project) or') }} <code class="bg-neutral-100 px-1 rounded">~/.cursor/mcp.json</code> {{ $t('(every project):') }}
          </p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="mcpCursorSnippet" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
          <button type="button" class="btn-secondary mt-2" @click="copySnippet(mcpCursorSnippet, 'cursor')">
            {{ copiedSnippet === 'cursor' ? $t("Copied!") : $t("Copy Cursor JSON") }}
          </button>
        </div>

        <div class="mt-4" v-if="mcpClientTab === 'vscode'">
          <p class="text-sm text-neutral-600">
            {{ $t('Add to') }} <code class="bg-neutral-100 px-1 rounded">.vscode/mcp.json</code> {{ $t('— note the') }}
            <code class="bg-neutral-100 px-1 rounded">servers</code> {{ $t('key, not') }}
            <code class="bg-neutral-100 px-1 rounded">mcpServers</code>{{ $t(". Pasting Cursor's shape here silently fails:") }}
          </p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="mcpVsCodeSnippet" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
          <button type="button" class="btn-secondary mt-2" @click="copySnippet(mcpVsCodeSnippet, 'vscode')">
            {{ copiedSnippet === 'vscode' ? $t("Copied!") : $t("Copy VS Code JSON") }}
          </button>
        </div>

        <div class="mt-4" v-if="mcpClientTab === 'chatgpt'">
          <p class="text-sm text-neutral-600">
            {{ $t("ChatGPT's MCP connectors (Developer mode) authenticate with OAuth or no authentication only — they cannot send an API-key header, so ChatGPT can't connect to FeedBear yet. OAuth support (one-click connect for ChatGPT and Claude) is on our roadmap. In the meantime, Claude Code, Claude Desktop, Cursor, and VS Code all work with the key above.") }}
          </p>
        </div>
      </div>
    </div>

    <Dialog v-model="showTokenModal" :title="$t('API key created')" custom-max-width-class="max-w-3xl">
      <div class="px-4 pt-5 pb-4 sm:px-6">
        <p class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
          {{ $t('This token is shown once and cannot be looked up later. Copy everything you need before you close this window — if you lose it, rotate the key to get a new one.') }}
        </p>

        <label class="label mt-4">{{ $t('API token') }}</label>
        <div class="flex items-stretch gap-2 mt-1">
          <div class="flex-1 min-w-0 flex items-center px-3 rounded-lg bg-neutral-100 border text-sm font-mono overflow-x-auto whitespace-nowrap">
            {{ createdToken }}
          </div>
          <button type="button" class="btn-main flex-shrink-0" @click="copyToken">
            {{ tokenCopied ? $t("Copied!") : $t("Copy") }}
          </button>
        </div>

        <div class="mt-6 pt-5 border-t" v-if="mcpAvailable">
          <h4 class="font-semibold text-neutral-900">{{ $t('Connect your AI assistant') }}</h4>
          <p class="text-sm text-neutral-600 mt-1">
            {{ $t('Pick your client and copy the setup below — your token is already filled in.') }}
          </p>

          <TabGroup wrap key-name="id" class="mt-3" :tabs="mcpClients" :value="mcpClientTab" @input="(v) => mcpClientTab = v" />

          <p class="text-sm text-neutral-600 mt-3">{{ mcpSetupHint }}</p>
          <div v-if="mcpSetupSnippet" class="mt-2">
            <div class="p-3 rounded-lg bg-neutral-100 border text-xs font-mono text-neutral-900 whitespace-pre-wrap break-words">{{ mcpSetupSnippet }}</div>
            <div class="flex justify-end mt-2">
              <button type="button" class="btn-secondary" @click="copySnippet(mcpSetupSnippet, 'modal-setup')">
                {{ copiedSnippet === 'modal-setup' ? $t("Copied!") : $t("Copy") }}
              </button>
            </div>
          </div>

          <p class="text-xs text-neutral-500 mt-3">
            {{ $t('Step-by-step instructions for every client stay on the settings page behind this window.') }}
          </p>
        </div>

        <p v-if="modalError" class="text-red-600 text-sm mt-3">{{ modalError }}</p>
      </div>
      <div class="px-4 py-3 sm:px-6 flex items-center justify-end border-t">
        <button type="button" class="btn-secondary" @click="closeTokenModal">
          {{ $t('Done') }}
        </button>
      </div>
    </Dialog>

    <!-- Inline Documentation -->
    <div class="mt-8 p-5 border rounded-xl">
      <TabGroup
        wrap
        :tabs="[{ name: 'quickstart', label: $t('Quick start') }, { name: 'endpoints', label: $t('Endpoints') }, { name: 'responses', label: $t('Response format') }]"
        :value="docsTab"
        @input="(v) => docsTab = v"
      />

      <!-- Quick Start Tab -->
      <div class="mt-4" v-if="docsTab === 'quickstart'">
        <div class="mt-3">
          <h4 class="font-semibold text-neutral-900">{{ $t('1. Create an API key') }}</h4>
          <p class="text-neutral-600 mt-1">{{ $t('Create a key below and copy the token. It is only shown once.') }}</p>
        </div>
        <div class="mt-4">
          <h4 class="font-semibold text-neutral-900">{{ $t('2. Authenticate your requests') }}</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t('Include your token in the') }} <code class="bg-neutral-100 px-1 rounded text-sm">Authorization</code> {{ $t('header as a Bearer token:') }}
          </p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="quickstartAuthCode" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
        </div>
        <div class="mt-4">
          <h4 class="font-semibold text-neutral-900">{{ $t('3. Make your first request') }}</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t('List your boards to verify everything works:') }}
          </p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="quickstartListCode" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
        </div>
        <div class="mt-4">
          <h4 class="font-semibold text-neutral-900">{{ $t('4. Create an item') }}</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t('Post a new item to a board (replace') }} <code class="bg-neutral-100 px-1 rounded text-sm">BOARD_ID</code> {{ $t('with the ID from step 3):') }}
          </p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="quickstartCreateCode" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
        </div>
      </div>

      <!-- Endpoints Tab -->
      <div class="mt-4" v-if="docsTab === 'endpoints'">
        <p class="text-neutral-600 mb-4">
          {{ $t('Base URL:') }} <code class="bg-neutral-100 px-1 rounded text-sm">{{ baseApiUrl }}</code>
        </p>

        <div class="mt-3">
          <h4 class="font-semibold text-neutral-900">GET /boards</h4>
          <p class="text-neutral-600 mt-1">{{ $t('List all boards in the project. Private boards are excluded by default — pass') }} <code class="bg-neutral-100 px-1 rounded text-sm">include_private=true</code> {{ $t('to include them.') }}</p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Params:') }} <code class="bg-neutral-100 px-1 rounded">limit</code>
            <code class="bg-neutral-100 px-1 rounded">cursor</code>
            <code class="bg-neutral-100 px-1 rounded">include_private</code>
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">boards:read</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">GET /boards/:board_id/items</h4>
          <p class="text-neutral-600 mt-1">{{ $t('List items on a board.') }}</p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Params:') }} <code class="bg-neutral-100 px-1 rounded">status_id</code>
            <code class="bg-neutral-100 px-1 rounded">search</code>
            <code class="bg-neutral-100 px-1 rounded">sort</code>
            <code class="bg-neutral-100 px-1 rounded">limit</code>
            <code class="bg-neutral-100 px-1 rounded">cursor</code>
          </p>
          <p class="text-sm text-neutral-500">
            {{ $t('Sort options:') }} <code class="bg-neutral-100 px-1 rounded">trend_score</code>
            <code class="bg-neutral-100 px-1 rounded">total_upvotes</code>
            <code class="bg-neutral-100 px-1 rounded">created_at</code>
            <code class="bg-neutral-100 px-1 rounded">comments_count</code>
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">items:read</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">GET /boards/:board_id/items/:id</h4>
          <p class="text-neutral-600 mt-1">{{ $t('Get a single item with full details.') }} {{ $t('Pass') }} <code class="bg-neutral-100 px-1 rounded">include_voters=true</code> {{ $t('to embed the first 50 voters with their names and emails.') }}</p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">items:read</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">POST /boards/:board_id/items</h4>
          <p class="text-neutral-600 mt-1">{{ $t('Create a new item on a board.') }}</p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Body:') }} <code class="bg-neutral-100 px-1 rounded">title</code> {{ $t('(required),') }}
            <code class="bg-neutral-100 px-1 rounded">content</code>
            <code class="bg-neutral-100 px-1 rounded">status_id</code>
            <code class="bg-neutral-100 px-1 rounded">external_id</code>
            <code class="bg-neutral-100 px-1 rounded">is_private</code>
            <code class="bg-neutral-100 px-1 rounded">comments_disabled</code>
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">items:write</code></p>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="createItemCode" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">PATCH /boards/:board_id/items/:id</h4>
          <p class="text-neutral-600 mt-1">{{ $t('Update an existing item.') }}</p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">items:write</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">POST /boards/:board_id/items/:id/merge</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t('Merge a duplicate item into another. Votes and comments move to the target, then the item redirects to it. Irreversible.') }}
          </p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Body:') }} <code class="bg-neutral-100 px-1 rounded">target_item_id</code> {{ $t('(required, may be on any board in the project)') }}
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">items:write</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">DELETE /boards/:board_id/items/:id</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t('Delete an item. If the item has merged posts, pass') }} <code class="bg-neutral-100 px-1 rounded">?force=true</code> {{ $t('to confirm.') }}
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">items:write</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">GET /statuses</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t("List the project's statuses (id, name, color, order, roadmap flags). Small list, not paginated.") }}
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">boards:read</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">GET /boards/:board_id/items/:item_id/comments</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t("List an item's comments, newest first. Replies carry") }} <code class="bg-neutral-100 px-1 rounded">parent_comment_id</code>{{ $t('; spam comments are excluded.') }}
          </p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Params:') }} <code class="bg-neutral-100 px-1 rounded">limit</code>
            <code class="bg-neutral-100 px-1 rounded">cursor</code>
            <code class="bg-neutral-100 px-1 rounded">include_private</code>
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">comments:read</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">GET /boards/:board_id/items/:item_id/voters</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t("List who voted on an item, newest vote first, with name, email and") }} <code class="bg-neutral-100 px-1 rounded">voted_at</code>.
            <code class="bg-neutral-100 px-1 rounded">kind</code> {{ $t('is account, on_behalf or email; anonymous votes have no identity and only appear as') }} <code class="bg-neutral-100 px-1 rounded">meta.anonymous_votes</code>.
          </p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Params:') }} <code class="bg-neutral-100 px-1 rounded">limit</code>
            <code class="bg-neutral-100 px-1 rounded">cursor</code>
            <code class="bg-neutral-100 px-1 rounded">include_private</code>
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">items:read</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">POST /boards/:board_id/items/:item_id/comments</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t('Add a comment to an item. The comment is attributed to the team member who created the API key and notifies participants like a regular comment. Returns') }} <code class="bg-neutral-100 px-1 rounded">409</code>
            {{ $t('if comments are disabled on the item.') }}
          </p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Body:') }} <code class="bg-neutral-100 px-1 rounded">comment[body]</code> {{ $t('(required, simple HTML allowed)') }}
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">comments:write</code></p>
        </div>

        <hr class="my-4 border-neutral-200">

        <div>
          <h4 class="font-semibold text-neutral-900">GET /changelog_entries</h4>
          <p class="text-neutral-600 mt-1">
            {{ $t('List published changelog entries, newest first. Drafts and scheduled entries are never included.') }}
          </p>
          <p class="text-sm text-neutral-500 mt-1">
            {{ $t('Params:') }} <code class="bg-neutral-100 px-1 rounded">limit</code>
            <code class="bg-neutral-100 px-1 rounded">cursor</code>
          </p>
          <p class="text-sm text-neutral-500">{{ $t('Scope:') }} <code class="bg-neutral-100 px-1 rounded">changelog:read</code></p>
        </div>

      </div>

      <!-- Response Format Tab -->
      <div class="mt-4" v-if="docsTab === 'responses'">
        <div class="mt-3">
          <h4 class="font-semibold text-neutral-900">{{ $t('Success (single item)') }}</h4>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="responseSingleCode" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
        </div>

        <div class="mt-4">
          <h4 class="font-semibold text-neutral-900">{{ $t('Success (list with pagination)') }}</h4>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="responseListCode" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
        </div>

        <div class="mt-4">
          <h4 class="font-semibold text-neutral-900">{{ $t('Error') }}</h4>
          <div class="sso-webhook-style">
            <prism-editor class="prism-code-editor rounded mt-2" v-model="responseErrorCode" :highlight="highlighter" :line-numbers="false" readonly></prism-editor>
          </div>
        </div>

        <div class="mt-4">
          <h4 class="font-semibold text-neutral-900">{{ $t('Status codes') }}</h4>
          <div class="mt-2 text-sm text-neutral-600 space-y-1">
            <p><code class="bg-neutral-100 px-1 rounded">200</code> {{ $t('Success') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">201</code> {{ $t('Created') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">204</code> {{ $t('Deleted (no body)') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">400</code> {{ $t('Bad request') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">401</code> {{ $t('Invalid or missing credentials') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">403</code> {{ $t('Insufficient scope or API disabled') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">404</code> {{ $t('Resource not found') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">409</code> {{ $t('Conflict (duplicate external_id or has merged posts)') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">422</code> {{ $t('Validation error') }}</p>
            <p><code class="bg-neutral-100 px-1 rounded">429</code> {{ $t('Rate limited (check Retry-After header)') }}</p>
          </div>
        </div>

        <div class="mt-4">
          <h4 class="font-semibold text-neutral-900">{{ $t('Rate limits') }}</h4>
          <p class="text-sm text-neutral-600 mt-1">
            {{ $t('600 requests per minute per API key, 1000 requests per minute per IP address.') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Create API Key -->
    <div class="mt-8 p-5 border rounded-xl">
      <h4 class="text-lg font-semibold text-neutral-900">{{ $t('Create API key') }}</h4>
      <p class="text-sm text-neutral-600 mt-1">
        {{ $t('Assign only the scopes this key needs.') }}
      </p>

      <div class="mt-4">
        <label class="label">{{ $t('Key name') }}</label>
        <input
          v-model.trim="newKeyName"
          type="text"
          class="input"
          maxlength="80"
          :placeholder="$t('Example: Production Sync Worker')"
        />
      </div>

      <div class="mt-4">
        <label class="label">{{ $t('Scopes') }}</label>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <label
            v-for="scope in availableScopes"
            :key="scope.value"
            class="flex items-start gap-2 border rounded p-3"
          >
            <input
              v-model="newKeyScopes"
              type="checkbox"
              :value="scope.value"
              class="mt-1"
            />
            <span>
              <strong class="text-neutral-800">{{ scope.value }}</strong><br>
              <span class="text-sm text-neutral-600">{{ scope.label }}</span>
              <span v-if="mcpAvailable" class="block text-xs text-neutral-500 mt-1">
                {{ $t('MCP tools:') }} {{ scope.mcpTools }}
              </span>
            </span>
          </label>
        </div>
      </div>

      <p v-if="formError" class="text-red-600 text-sm mt-3">{{ formError }}</p>

      <button
        type="button"
        class="btn-main mt-4"
        :disabled="isCreatingKey"
        @click="createKey"
      >
        {{ isCreatingKey ? $t("Creating...") : $t("Create API key") }}
      </button>
    </div>

    <!-- API Keys Table -->
    <div class="mt-8">
      <div class="flex items-center justify-between">
        <h4 class="text-lg font-semibold text-neutral-900">{{ $t('API keys') }}</h4>
        <button type="button" class="btn-secondary" :disabled="isLoadingKeys" @click="fetchKeys">
          {{ isLoadingKeys ? $t("Refreshing...") : $t("Refresh") }}
        </button>
      </div>

      <p v-if="keysError" class="text-red-600 text-sm mt-2">{{ keysError }}</p>

      <div class="mt-4 border rounded-xl overflow-x-auto">
        <table class="w-full min-w-[680px]">
          <thead class="bg-neutral-100 text-left">
            <tr>
              <th class="px-4 py-3 text-sm font-semibold text-neutral-700">{{ $t('Name') }}</th>
              <th class="px-4 py-3 text-sm font-semibold text-neutral-700">{{ $t('Key ID') }}</th>
              <th class="px-4 py-3 text-sm font-semibold text-neutral-700">{{ $t('Scopes') }}</th>
              <th class="px-4 py-3 text-sm font-semibold text-neutral-700">{{ $t('Last used') }}</th>
              <th class="px-4 py-3 text-sm font-semibold text-neutral-700">{{ $t('Status') }}</th>
              <th class="px-4 py-3 text-sm font-semibold text-neutral-700">{{ $t('Actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="keys.length === 0">
              <td class="px-4 py-5 text-sm text-neutral-600" colspan="6">{{ $t('No keys yet.') }}</td>
            </tr>
            <tr v-for="key in keys" :key="key.id" class="border-t">
              <td class="px-4 py-4 text-sm text-neutral-800">{{ key.name }}</td>
              <td class="px-4 py-4 text-sm text-neutral-700">{{ key.key_id }}</td>
              <td class="px-4 py-4 text-sm text-neutral-700">
                <span class="inline-flex flex-wrap gap-1">
                  <code
                    v-for="scope in key.scopes"
                    :key="`${key.id}-${scope}`"
                    class="px-2 py-1 bg-neutral-100 rounded text-xs"
                  >
                    {{ scope }}
                  </code>
                </span>
              </td>
              <td class="px-4 py-4 text-sm text-neutral-700">
                <div>{{ formatDate(key.last_used_at) }}</div>
                <div v-if="key.last_used_ip" class="text-xs text-neutral-500">{{ key.last_used_ip }}</div>
              </td>
              <td class="px-4 py-4 text-sm">
                <span
                  class="inline-flex px-2 py-1 rounded text-xs font-semibold"
                  :class="key.active ? 'bg-green-100 text-green-700' : 'bg-neutral-200 text-neutral-700'"
                >
                  {{ key.active ? $t("Active") : $t("Revoked") }}
                </span>
              </td>
              <td class="px-4 py-4 text-sm">
                <div class="flex items-center gap-3" v-if="key.active">
                  <button
                    type="button"
                    class="text-neutral-700 hover:text-project-500"
                    @click="confirmAction('rotate', key)"
                  >
                    {{ $t('Rotate') }}
                  </button>
                  <button
                    type="button"
                    class="text-red-600 hover:text-red-500"
                    @click="confirmAction('revoke', key)"
                  >
                    {{ $t('Revoke') }}
                  </button>
                </div>
                <div v-else>
                  <button
                    type="button"
                    class="text-red-600 hover:text-red-500"
                    @click="confirmAction('delete', key)"
                  >
                    {{ $t('Delete') }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Help -->
    <div class="mt-8">
      <h4 class="font-semibold text-neutral-900">{{ $t('Questions?') }}</h4>
      <p class="text-neutral-600 mt-1 text-sm">
        {{ $t('If you have any questions or issues, send an email to') }}
        <a href="mailto:support@feedbear.com" target="_blank" class="text-project-500 hover:underline">support@feedbear.com</a>.
      </p>
    </div>

    <!-- Revoke Confirmation -->
    <Dialog v-model="showRevokeModal" :title="$t('Revoke API key')" custom-width-class="w-3/12">
      <div class="p-4 pt-2 sm:px-6">
        <div class="p-2 pl-0">
          {{ $t('Are you sure you want to revoke') }} <strong>{{ confirmKey ? confirmKey.name : '' }}</strong>?<br>
          {{ $t('This key will immediately stop working and cannot be re-enabled.') }}
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <a
            href="#"
            class="btn-secondary whitespace-nowrap"
            @click.prevent="showRevokeModal = false"
          >
            {{ $t('Cancel') }}
          </a>
          <a
            href="#"
            class="btn-danger whitespace-nowrap"
            @click.prevent="revokeKey"
          >
            {{ $t('Revoke key') }}
          </a>
        </div>
      </div>
    </Dialog>

    <!-- Rotate Confirmation -->
    <Dialog v-model="showRotateModal" :title="$t('Rotate API key')" custom-width-class="w-3/12">
      <div class="p-4 pt-2 sm:px-6">
        <div class="p-2 pl-0">
          {{ $t('Are you sure you want to rotate') }} <strong>{{ confirmKey ? confirmKey.name : '' }}</strong>?<br>
          {{ $t('The existing token will stop working immediately and a new one will be generated.') }}
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <a
            href="#"
            class="btn-secondary whitespace-nowrap"
            @click.prevent="showRotateModal = false"
          >
            {{ $t('Cancel') }}
          </a>
          <a
            href="#"
            class="btn-danger whitespace-nowrap"
            @click.prevent="rotateKey"
          >
            {{ $t('Rotate key') }}
          </a>
        </div>
      </div>
    </Dialog>

    <!-- Delete Confirmation -->
    <Dialog v-model="showDeleteModal" :title="$t('Delete API key')" custom-width-class="w-3/12">
      <div class="p-4 pt-2 sm:px-6">
        <div class="p-2 pl-0">
          {{ $t('Are you sure you want to delete') }} <strong>{{ confirmKey ? confirmKey.name : '' }}</strong>?<br>
          {{ $t('This action cannot be undone.') }}
        </div>
        <div class="flex justify-end gap-2 mt-2">
          <a
            href="#"
            class="btn-secondary whitespace-nowrap"
            @click.prevent="showDeleteModal = false"
          >
            {{ $t('Cancel') }}
          </a>
          <a
            href="#"
            class="btn-danger whitespace-nowrap"
            @click.prevent="deleteKey"
          >
            {{ $t('Delete key') }}
          </a>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import 'vue-prism-editor/dist/prismeditor.min.css';
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism-tomorrow.css";
import Dialog from "../../components/dialog";
import TabGroup from "../../components/tab_group";

export default {
  components: {
    Dialog,
    TabGroup,
  },
  props: {
    apiEnabled: {
      type: [Boolean, String],
      required: true,
    },
    apiKeysUrl: {
      type: String,
      required: true,
    },
    apiSettingsUrl: {
      type: String,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
    projectDomain: {
      type: String,
      default: "",
    },
    mcpAvailable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      keys: [],
      createdToken: "",
      showTokenModal: false,
      tokenCopied: false,
      showRevokeModal: false,
      showRotateModal: false,
      showDeleteModal: false,
      confirmKey: null,
      newKeyName: "",
      newKeyScopes: ["boards:read", "items:read", "items:write"],
      isLoadingKeys: false,
      isCreatingKey: false,
      isUpdatingSetting: false,
      // One message per place it can be shown, so a failed load never reads as a
      // failed create (and vice versa).
      toggleError: "",
      mcpError: "",
      formError: "",
      keysError: "",
      modalError: "",
      docsTab: "quickstart",
      // Claude Code first: it is the client whose header auth reliably works on
      // a personal plan, so it is the safest default landing tab.
      mcpClientTab: "claude-code",
      copiedSnippet: "",
      apiEnabledState: this.apiEnabled === true || this.apiEnabled === "true",
      availableScopes: [
        {
          value: "boards:read",
          label: this.$t("List boards, statuses, and tags for the project."),
          mcpTools: "feedbear_list_boards, feedbear_list_statuses, feedbear_list_tags",
        },
        {
          value: "items:read",
          label: this.$t("Read board items, including who voted on them (names and emails)."),
          mcpTools: "feedbear_search_posts, feedbear_get_post, feedbear_list_voters",
        },
        {
          value: "items:write",
          label: this.$t("Create, update, merge, and delete board items."),
          mcpTools: "feedbear_create_post, feedbear_update_post, feedbear_merge_post, feedbear_tag_post, feedbear_untag_post",
        },
        {
          value: "items:moderate",
          label: this.$t("Moderate board items: spam flags and team-only internal notes."),
          mcpTools: "feedbear_set_spam, feedbear_set_internal_notes",
        },
        {
          value: "comments:read",
          label: this.$t("Read comments on items."),
          mcpTools: "feedbear_list_comments",
        },
        {
          value: "comments:write",
          label: this.$t("Add comments to items, posted as the key's creator."),
          mcpTools: "feedbear_add_comment",
        },
        {
          value: "changelog:read",
          label: this.$t("Read published changelog entries and changelog labels."),
          mcpTools: "feedbear_list_changelog_entries, feedbear_list_changelog_labels",
        },
        {
          value: "changelog:write",
          label: this.$t("Create draft changelog entries (never published by the key)."),
          mcpTools: "feedbear_create_changelog_entry",
        },
      ],
      // Derived from the tools' REQUIRED_SCOPES (app/services/mcp/tools/*.rb):
      // these four scopes cover exactly the six read tools. Mcp::ToolRegistry.for
      // hides the rest, so a read-only key yields an assistant without write tools.
      mcpReadScopes: [
        "boards:read",
        "items:read",
        "comments:read",
        "changelog:read",
      ],
      mcpFullScopes: [
        "boards:read",
        "items:read",
        "items:write",
        "items:moderate",
        "comments:read",
        "comments:write",
        "changelog:read",
        "changelog:write",
      ],
      mcpToolGroups: [
        {
          title: this.$t("Read-only key"),
          scopes: "boards:read, items:read, comments:read, changelog:read",
          tools: [
            { name: "feedbear_list_boards", label: this.$t("list your feedback boards") },
            { name: "feedbear_list_statuses", label: this.$t("list your post statuses") },
            { name: "feedbear_list_tags", label: this.$t("list a board's tags") },
            { name: "feedbear_search_posts", label: this.$t("search and list feedback posts") },
            { name: "feedbear_get_post", label: this.$t("read one post in full") },
            { name: "feedbear_list_comments", label: this.$t("read the discussion on a post") },
            { name: "feedbear_list_voters", label: this.$t("list who voted on a post, with names and email addresses, to follow up when it ships") },
            { name: "feedbear_list_changelog_entries", label: this.$t("read published changelog entries") },
            { name: "feedbear_list_changelog_labels", label: this.$t("list your changelog labels") },
          ],
        },
        {
          title: this.$t("Full-access key adds"),
          scopes: "items:write, items:moderate, comments:write, changelog:write",
          tools: [
            { name: "feedbear_create_post", label: this.$t("create a post (nobody is notified)") },
            { name: "feedbear_update_post", label: this.$t("edit a post, move it, or change its status (voters are not notified)") },
            { name: "feedbear_merge_post", label: this.$t("merge a duplicate post into another, moving its votes and comments (irreversible)") },
            { name: "feedbear_tag_post", label: this.$t("add a team-facing tag to a post") },
            { name: "feedbear_untag_post", label: this.$t("remove a tag from a post") },
            { name: "feedbear_add_comment", label: this.$t("publish a public comment as the key's creator, emailing the post's participants") },
            { name: "feedbear_create_changelog_entry", label: this.$t("draft a changelog entry (a team member publishes it; nobody is notified)") },
            { name: "feedbear_set_spam", label: this.$t("mark or unmark a post as spam, hiding it from the public board") },
            { name: "feedbear_set_internal_notes", label: this.$t("replace the team-only internal notes on a post") },
          ],
        },
      ],
      mcpClients: [
        { id: "claude-code", label: "Claude Code" },
        { id: "claude-desktop", label: "Claude Desktop" },
        { id: "cursor", label: "Cursor" },
        { id: "vscode", label: "VS Code" },
        { id: "chatgpt", label: "ChatGPT" },
      ],
    };
  },

  computed: {
    baseApiUrl() {
      return "https://" + (this.projectDomain || "app.feedbear.com") + "/api/v1";
    },

    tokenPlaceholder() {
      return this.createdToken || "YOUR_API_TOKEN";
    },

    quickstartAuthCode() {
      return "Authorization: Bearer " + this.tokenPlaceholder;
    },

    quickstartListCode() {
      return 'curl -H "Authorization: Bearer ' + this.tokenPlaceholder + '" \\\n  ' + this.baseApiUrl + '/boards';
    },

    quickstartCreateCode() {
      return 'curl -X POST \\\n  -H "Authorization: Bearer ' + this.tokenPlaceholder + '" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"item": {"title": "My first API item", "content": "<p>Created via API</p>"}}\' \\\n  ' + this.baseApiUrl + '/boards/BOARD_ID/items';
    },

    createItemCode() {
      return 'curl -X POST \\\n  -H "Authorization: Bearer YOUR_API_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"item": {\n    "title": "Feature request title",\n    "content": "<p>Description here</p>",\n    "status_id": "STATUS_UUID",\n    "external_id": "your-system-id-123"\n  }}\' \\\n  ' + this.baseApiUrl + '/boards/BOARD_ID/items';
    },

    responseSingleCode() {
      return '{\n  "data": {\n    "id": "abc-123",\n    "title": "My item",\n    "content": "<p>Description</p>",\n    "status": { "id": "...", "name": "Under review", "color": "#f59e0b" },\n    "board": { "id": "...", "name": "Feature requests", "slug": "feature-requests" },\n    "user": { "id": 1, "name": "Jane", "handle": "jane-123" },\n    "cached_votes_up": 12,\n    "comments_count": 3,\n    "created_at": "2026-03-24T12:00:00Z"\n  },\n  "meta": { "request_id": "req_..." }\n}';
    },

    responseListCode() {
      return '{\n  "data": [\n    { "id": "abc-123", "title": "Item one", "..." : "..." },\n    { "id": "def-456", "title": "Item two", "..." : "..." }\n  ],\n  "meta": {\n    "request_id": "req_...",\n    "has_more": true,\n    "next_cursor": "eyJ..."\n  }\n}';
    },

    responseErrorCode() {
      return '{\n  "error": {\n    "code": "validation_error",\n    "message": "Title can\'t be blank",\n    "details": { "title": ["can\'t be blank"] }\n  },\n  "meta": { "request_id": "req_..." }\n}';
    },

    // Canonical MCP URL — the endpoint answers on any project domain, but we
    // advertise one URL everywhere (spec §3).
    mcpUrl() {
      return "https://app.feedbear.com/mcp";
    },

    mcpAuthHeaderSnippet() {
      return "Bearer " + this.tokenPlaceholder;
    },

    mcpClaudeCodeSnippet() {
      return 'claude mcp add --transport http feedbear ' + this.mcpUrl + ' \\\n  --header "Authorization: Bearer ' + this.tokenPlaceholder + '"';
    },

    mcpCursorSnippet() {
      return '{\n  "mcpServers": {\n    "feedbear": {\n      "url": "' + this.mcpUrl + '",\n      "headers": {\n        "Authorization": "Bearer ' + this.tokenPlaceholder + '"\n      }\n    }\n  }\n}';
    },

    // VS Code's mcp.json uses a "servers" root key and a "type" field —
    // pasting Cursor's "mcpServers" shape into VS Code silently fails.
    mcpVsCodeSnippet() {
      return '{\n  "servers": {\n    "feedbear": {\n      "type": "http",\n      "url": "' + this.mcpUrl + '",\n      "headers": {\n        "Authorization": "Bearer ' + this.tokenPlaceholder + '"\n      }\n    }\n  }\n}';
    },

    // Claude Desktop has no header field for remote servers on personal plans,
    // so it goes through the mcp-remote stdio bridge. Shape and flags follow
    // mcp-remote's own README: the header value carries no space around the
    // colon and lives in env, because some clients drop spaces inside args.
    // http-only because /mcp is stateless Streamable HTTP with no SSE stream.
    mcpClaudeDesktopSnippet() {
      return '{\n  "mcpServers": {\n    "feedbear": {\n      "command": "npx",\n      "args": [\n        "mcp-remote",\n        "' + this.mcpUrl + '",\n        "--transport",\n        "http-only",\n        "--header",\n        "Authorization:${FEEDBEAR_AUTH}"\n      ],\n      "env": {\n        "FEEDBEAR_AUTH": "Bearer ' + this.tokenPlaceholder + '"\n      }\n    }\n  }\n}';
    },

    // Drives the condensed setup block inside the token reveal modal, so the
    // real token is copyable in one pass without closing the modal first.
    mcpSetupSnippet() {
      switch (this.mcpClientTab) {
        case "claude-code":
          return this.mcpClaudeCodeSnippet;
        case "claude-desktop":
          return this.mcpClaudeDesktopSnippet;
        case "cursor":
          return this.mcpCursorSnippet;
        case "vscode":
          return this.mcpVsCodeSnippet;
        default:
          return "";
      }
    },

    mcpSetupHint() {
      switch (this.mcpClientTab) {
        case "claude-code":
          return this.$t("Run this in your terminal.");
        case "claude-desktop":
          return this.$t("Add this to claude_desktop_config.json (Settings → Developer → Edit Config), then quit and reopen Claude Desktop. Requires Node.js.");
        case "cursor":
          return this.$t("Add this to .cursor/mcp.json in your project, or ~/.cursor/mcp.json for every project.");
        case "vscode":
          return this.$t("Add this to .vscode/mcp.json — note the \"servers\" key, not \"mcpServers\".");
        default:
          return this.$t("ChatGPT connectors cannot send an API-key header, so ChatGPT cannot connect to FeedBear yet. Use Claude Code, Claude Desktop, Cursor, or VS Code.");
      }
    },
  },

  mounted() {
    this.fetchKeys();
  },

  methods: {
    highlighter(code) {
      return highlight(code, languages.js);
    },

    baseKeysUrl() {
      return this.apiKeysUrl.replace(/\/$/, "");
    },

    fetchKeys() {
      this.isLoadingKeys = true;
      this.keysError = "";

      Rails.ajax({
        url: this.baseKeysUrl(),
        type: "GET",
        dataType: "json",
        success: (data) => {
          this.keys = data.data || [];
          this.isLoadingKeys = false;
        },
        error: () => {
          this.keysError = this.$t("Failed to load API keys.");
          this.isLoadingKeys = false;
        },
      });
    },

    toggleApiEnabled() {
      this.isUpdatingSetting = true;
      this.toggleError = "";

      const data = new FormData();
      data.append("api_enabled", this.apiEnabledState);
      data.append("authenticity_token", this.token);

      Rails.ajax({
        url: this.apiSettingsUrl,
        type: "PATCH",
        data,
        dataType: "json",
        success: (response) => {
          const payload = response && response.data ? response.data : {};
          this.apiEnabledState = payload.api_enabled === true;
          this.$emit("apiEnabledUpdated", this.apiEnabledState);
          this.isUpdatingSetting = false;
        },
        error: () => {
          this.apiEnabledState = !this.apiEnabledState;
          this.toggleError = this.$t("Failed to update API settings.");
          this.isUpdatingSetting = false;
        },
      });
    },

    createKey() {
      this.formError = "";

      if (!this.newKeyName) {
        this.formError = this.$t("Please provide a key name.");
        return;
      }

      if (!this.newKeyScopes.length) {
        this.formError = this.$t("Please select at least one scope.");
        return;
      }

      this.submitKey(this.newKeyName, this.newKeyScopes, true, "form");
    },

    createMcpKey(mode) {
      this.mcpError = "";

      const full = mode === "full";
      const name = full ? "MCP (full access)" : "MCP (read-only)";

      // Key names are not unique server-side, so without this a second click
      // silently piles up identical keys against the 10-active-key limit.
      if (this.keys.some((key) => key.active && key.name === name)) {
        this.mcpError = this.$t(
          'You already have an active key named "{name}". Rotate it in the API keys table below to get a fresh token, or revoke it first.',
          { name }
        );
        return;
      }

      this.submitKey(name, full ? this.mcpFullScopes : this.mcpReadScopes, false, "mcp");
    },

    submitKey(name, scopes, resetForm, errorTarget) {
      this.isCreatingKey = true;

      const data = new FormData();
      data.append("authenticity_token", this.token);
      data.append("project_api_key[name]", name);
      scopes.forEach((scope) => {
        data.append("project_api_key[scopes][]", scope);
      });

      Rails.ajax({
        url: this.baseKeysUrl(),
        type: "POST",
        data,
        dataType: "json",
        success: (response) => {
          if (response.data) {
            this.keys = [response.data, ...this.keys];
          }

          const credentials = response && response.credentials ? response.credentials : {};
          this.createdToken = credentials.token || "";
          this.tokenCopied = false;
          this.modalError = "";
          if (this.createdToken) this.showTokenModal = true;
          if (resetForm) {
            this.newKeyName = "";
            this.newKeyScopes = ["boards:read", "items:read", "items:write"];
          }
          this.isCreatingKey = false;
        },
        error: (response) => {
          let serverError = null;
          if (response && response[0] && response[0].body && response[0].body[0]) {
            serverError = response[0].body[0];
          }
          const message = (serverError && serverError.error) || this.$t("Failed to create API key.");
          if (errorTarget === "mcp") {
            this.mcpError = message;
          } else {
            this.formError = message;
          }
          this.isCreatingKey = false;
        },
      });
    },

    copySnippet(text, id) {
      const markCopied = () => {
        this.copiedSnippet = id;
        setTimeout(() => {
          if (this.copiedSnippet === id) this.copiedSnippet = "";
        }, 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(markCopied).catch(() => {
          this.fallbackCopyText(text, markCopied);
        });
      } else {
        this.fallbackCopyText(text, markCopied);
      }
    },

    fallbackCopyText(text, onSuccess) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        onSuccess();
      } catch (_error) {
        this.setCopyError(this.$t("Copy failed. Please select the text and copy it manually."));
      }
      document.body.removeChild(textarea);
    },

    // Copy affordances exist both in the MCP section and inside the reveal
    // modal, which covers it — report the failure wherever the user is looking.
    setCopyError(message) {
      if (this.showTokenModal) {
        this.modalError = message;
      } else {
        this.mcpError = message;
      }
    },

    confirmAction(action, key) {
      this.confirmKey = key;
      if (action === 'revoke') {
        this.showRevokeModal = true;
      } else if (action === 'rotate') {
        this.showRotateModal = true;
      } else if (action === 'delete') {
        this.showDeleteModal = true;
      }
    },

    revokeKey() {
      const key = this.confirmKey;
      if (!key || !key.active) return;

      this.showRevokeModal = false;
      this.keysError = "";

      const data = new FormData();
      data.append("authenticity_token", this.token);

      Rails.ajax({
        url: `${this.baseKeysUrl()}/${key.id}`,
        type: "DELETE",
        data,
        dataType: "json",
        success: (response) => {
          const updatedKey = response.data;
          this.keys = this.keys.map((existing) => {
            return existing.id === updatedKey.id ? updatedKey : existing;
          });
          this.confirmKey = null;
        },
        error: () => {
          this.keysError = this.$t("Failed to revoke API key.");
          this.confirmKey = null;
        },
      });
    },

    rotateKey() {
      const key = this.confirmKey;
      if (!key || !key.active) return;

      this.showRotateModal = false;
      this.keysError = "";

      const data = new FormData();
      data.append("authenticity_token", this.token);

      Rails.ajax({
        url: `${this.baseKeysUrl()}/${key.id}/rotate`,
        type: "POST",
        data,
        dataType: "json",
        success: (response) => {
          if (response.data) {
            this.keys = [response.data, ...this.keys.map((existing) => {
            if (existing.id === key.id) {
              return { ...existing, active: false, revoked_at: new Date().toISOString() };
            }
            return existing;
          })];
          }
          const credentials = response && response.credentials ? response.credentials : {};
          this.createdToken = credentials.token || "";
          this.tokenCopied = false;
          this.modalError = "";
          if (this.createdToken) this.showTokenModal = true;
          this.confirmKey = null;
        },
        error: () => {
          this.keysError = this.$t("Failed to rotate API key.");
          this.confirmKey = null;
        },
      });
    },

    deleteKey() {
      const key = this.confirmKey;
      if (!key) return;

      this.showDeleteModal = false;
      this.keysError = "";

      const data = new FormData();
      data.append("authenticity_token", this.token);

      Rails.ajax({
        url: `${this.baseKeysUrl()}/${key.id}`,
        type: "DELETE",
        data,
        dataType: "json",
        success: () => {
          this.keys = this.keys.filter((existing) => existing.id !== key.id);
          this.confirmKey = null;
        },
        error: () => {
          this.keysError = this.$t("Failed to delete API key.");
          this.confirmKey = null;
        },
      });
    },

    copyToken() {
      if (!this.createdToken) return;

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.createdToken).then(() => {
          this.tokenCopied = true;
        }).catch(() => {
          this.fallbackCopy();
        });
      } else {
        this.fallbackCopy();
      }
    },

    fallbackCopy() {
      const textarea = document.createElement("textarea");
      textarea.value = this.createdToken;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        this.tokenCopied = true;
      } catch (_error) {
        this.setCopyError(this.$t("Copy failed. Please select the token and copy it manually."));
      }
      document.body.removeChild(textarea);
    },

    // Deliberately keeps createdToken so the setup snippets on the page behind
    // the modal stay filled in with the real token. Clearing it here used to
    // silently downgrade every snippet to the literal YOUR_API_TOKEN, with no
    // way to recover the token short of rotating the key. It is still shown
    // only once: a page reload drops it.
    closeTokenModal() {
      this.showTokenModal = false;
      this.tokenCopied = false;
      this.modalError = "";
    },

    formatDate(value) {
      if (!value) return this.$t("Never");
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return this.$t("Never");
      return date.toLocaleString();
    },
  },
};
</script>

<style scoped lang="scss">
.area--disabled {
  opacity: 0.4;
  pointer-events: none;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
}

.button--toggle {
  position: relative;
  width: 74px;
  height: 36px;
  overflow: hidden;
  margin-right: 0.5rem;
}

.button--toggle,
.button--toggle .layer {
  border-radius: 100px;
}

.checkbox {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 3;
}

.knobs,
.layer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.knobs {
  z-index: 2;
}

.layer {
  width: 100%;
  background-color: #f5f5f5;
  transition: 0.3s ease all;
  z-index: 1;
  border: 1px solid gainsboro;
}

.button--toggle .knobs:before {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 28px;
  height: 28px;
  background-color: #d2d2d2;
  border-radius: 50%;
  transition: 0.3s ease all, left 0.3s cubic-bezier(0.18, 0.89, 0.35, 1.15);
}

.button--toggle .checkbox:active + .knobs:before {
  width: 46px;
  border-radius: 100px;
}

.button--toggle .checkbox:checked:active + .knobs:before {
  margin-left: -26px;
}

.button--toggle .checkbox:checked + .knobs:before {
  content: "";
  left: 42px;
  background-color: #3bd087;
}

.button--toggle .checkbox:checked ~ .layer {
  background-color: #d1f0e1;
  border-color: #a3e6c6;
}

/* Animations */
.fromLeft-move {
  transition: all 250ms;
}
.fromLeft-enter-active,
.fromLeft-leave-active {
  transition: all 250ms;
}
.fromLeft-enter,
.fromLeft-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

</style>
